import { z } from 'zod';

import { SchemaValidationFailedError } from '@kilat/shared';
import { callFunction } from './_lazy.js';

/**
 * Typed wrappers around the two bootstrap callables. Feature code uses these
 * instead of importing `firebase/functions` directly.
 *
 * Inputs are validated client-side too (mirror of server schema) so the user
 * gets immediate feedback before a round-trip.
 */

// ── tenantBootstrap ───────────────────────────────────────────────────────

export const TenantBootstrapInputSchema = z.object({
  businessName: z.string().min(1).max(100),
  ownerName: z.string().min(1).max(100),
  email: z.string().email(),
  /**
   * E.164 phone (e.g. `+60123456789`). The signup form runs
   * `phone.normaliseMalaysianPhone` on the raw input and submits the
   * normalised value here. Server schema is permissive on format —
   * the strict shape is enforced client-side so the user gets
   * immediate feedback.
   */
  phone: z.string().min(8).max(20),
  password: z.string().min(8).max(128),
  agentCode: z.string().optional(),
  originHost: z.string().min(1),
  locale: z.enum(['en', 'ms', 'zh', 'ta']).default('en'),
});
export type TenantBootstrapInput = z.infer<typeof TenantBootstrapInputSchema>;

export const TenantBootstrapOutputSchema = z.object({
  tenantId: z.string(),
  slug: z.string(),
  signInToken: z.string(),
});
export type TenantBootstrapOutput = z.infer<typeof TenantBootstrapOutputSchema>;

export async function callTenantBootstrap(
  input: TenantBootstrapInput,
): Promise<TenantBootstrapOutput> {
  const parsed = TenantBootstrapInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('tenantBootstrap.input', parsed.error.flatten());
  }
  const result = await callFunction<TenantBootstrapInput, TenantBootstrapOutput>(
    'tenantBootstrap',
    parsed.data,
  );
  const out = TenantBootstrapOutputSchema.safeParse(result);
  if (!out.success) {
    throw new SchemaValidationFailedError('tenantBootstrap.output', out.error.flatten());
  }
  return out.data;
}

// ── agentBootstrap ────────────────────────────────────────────────────────

export const AgentBootstrapInputSchema = z.object({
  name: z.string().min(1).max(100),
  phone: z.string().min(7).max(20),
  email: z.string().email(),
  locale: z.enum(['en', 'ms', 'zh', 'ta']).default('en'),
  pitch: z.string().max(500).optional(),
  originHost: z.string().min(1),
  /**
   * Sprint 84 — optional recruiter referral code (`RC-XXXX`). When the
   * agent landed via `?ref=RC-XXXX` and the server validates the code,
   * the resulting agent doc is stamped with `acquisition.channel =
   * 'recruiter'` for life. Invalid codes are silently ignored server-
   * side so a typo doesn't block signup.
   */
  recruiterCode: z.string().min(3).max(20).optional(),
});
export type AgentBootstrapInput = z.infer<typeof AgentBootstrapInputSchema>;

export const AgentBootstrapOutputSchema = z.object({
  agentId: z.string(),
  code: z.string(),
  signInToken: z.string(),
  /**
   * Confirms server-side what acquisition channel was actually stamped
   * on the agent doc. Important for the success card: shows
   * "Attributed to {recruiterName}" only when the server actually
   * accepted the recruiter code (a typo'd code falls back to direct
   * without complaint).
   */
  attribution: z.discriminatedUnion('channel', [
    z.object({ channel: z.literal('direct') }),
    z.object({ channel: z.literal('recruiter'), recruiterName: z.string() }),
  ]),
});
export type AgentBootstrapOutput = z.infer<typeof AgentBootstrapOutputSchema>;

export async function callAgentBootstrap(
  input: AgentBootstrapInput,
): Promise<AgentBootstrapOutput> {
  const parsed = AgentBootstrapInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('agentBootstrap.input', parsed.error.flatten());
  }
  const result = await callFunction<AgentBootstrapInput, AgentBootstrapOutput>(
    'agentBootstrap',
    parsed.data,
  );
  const out = AgentBootstrapOutputSchema.safeParse(result);
  if (!out.success) {
    throw new SchemaValidationFailedError('agentBootstrap.output', out.error.flatten());
  }
  return out.data;
}
